import {Separator} from 'platform/components';
import {Heading, Hide, VStack} from 'platform/foundation';

import {filter, groupBy, indexBy, map, pipe} from 'ramda';

import {useListCustomFieldPropertyQuery} from '@dms/api/core';
import {TenantFieldDefinitionResponseBody, useGetFieldDefinitionListQuery} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {isEntityFeatureFlagEnabled, usePermissions} from '@dms/shared';

import {isLastIndexInArray} from 'shared/web';

import {ResourceDefinitionList} from './components/ResourceDefinitionList';
import {
  CUSTOM_FIELDS_RESOURCES,
  CustomFieldsResource,
  CustomFieldsResourcePermission,
} from './constants/customFieldsResources';

export function CustomFieldsDefinitions() {
  const {
    data: definitionList,
    isLoading: isDefinitionListLoading,
    isError: isDefinitionListError,
  } = useGetFieldDefinitionListQuery({type: 'tenant'});

  const {
    data: customFieldPropertyList,
    isLoading: isCustomFieldPropertyListLoading,
    isError: isCustomFieldPropertyListError,
  } = useListCustomFieldPropertyQuery();

  const customFieldPropertiesById = indexBy(
    (item) => item.customFieldDefinitionId,
    customFieldPropertyList ?? []
  );

  const [
    canReadVehicle,
    canReadInterest,
    canReadBusinessCase,
    canReadServiceCase,
    canReadWorkshopInterest,
    canReadRentalOrder,
    canReadEmployee,
  ] = usePermissions({
    permissionKeys: [
      'vehicleRead',
      'interestRead',
      'businessCaseRead',
      'serviceCaseRead',
      'workshopInterestRead',
      'readRentalOrder',
      'employeeRead',
    ],
    scopes: {
      interestRead: {
        // there is no specific interest record in the settings context
        participation: 'SKIP',
        // there is no specific interest record in the settings context
        branchId: 'SKIP',
      },
      serviceCaseRead: {
        // there is no specific service case record in the settings context
        branchId: 'SKIP',
        // there is no specific service case record in the settings context
        serviceCaseType: 'SKIP',
      },
      workshopInterestRead: {
        // there is no specific workshop interest record in the settings context
        participation: 'SKIP',
        // there is no specific workshop interest record in the settings context
        branchId: 'SKIP',
      },
    },
  });

  const isResourcePermissionGranted: Record<CustomFieldsResourcePermission, boolean> = {
    vehicleRead: canReadVehicle,
    interestRead: canReadInterest,
    businessCaseRead: canReadBusinessCase,
    serviceCaseRead: canReadServiceCase,
    workshopInterestRead: canReadWorkshopInterest,
    readRentalOrder: canReadRentalOrder,
    employeeRead: canReadEmployee,
  };

  const isLoading = isDefinitionListLoading || isCustomFieldPropertyListLoading;
  const isError = isDefinitionListError || isCustomFieldPropertyListError;

  const definitionListByResourceId = groupBy(
    (definition: TenantFieldDefinitionResponseBody) => definition.resource.id,
    definitionList ?? []
  );

  const customFieldsDefinitions = pipe(
    filter(
      (item: CustomFieldsResource) =>
        isEntityFeatureFlagEnabled(item.resourceId) && isResourcePermissionGranted[item.permission]
    ),
    map((item) => ({
      ...item,
      definitions: definitionListByResourceId[item.resourceId] ?? [],
    }))
  )(CUSTOM_FIELDS_RESOURCES);

  return (
    <SettingsTemplate
      isLoading={isLoading}
      isError={isError}
      data-testid={testIds.settings.customFields('template')}
      header={{
        title: i18n.t('page.generalSettings.labels.customFields'),
        subtitle: i18n.t('page.generalSettings.labels.customFieldsDescriptions'),
      }}
    >
      <SettingsSection>
        {customFieldsDefinitions.map(({resourceId, definitions, label}, index) => (
          <>
            <VStack spacing={2} key={resourceId}>
              <Heading size={4}>{label}</Heading>

              <ResourceDefinitionList
                definitions={definitions}
                resourceId={resourceId}
                data-testid={testIds.settings.customFields(resourceId)}
                customFieldPropertiesById={customFieldPropertiesById}
              />
            </VStack>

            <Hide when={isLastIndexInArray(customFieldsDefinitions, index)}>
              <Separator />
            </Hide>
          </>
        ))}
      </SettingsSection>
    </SettingsTemplate>
  );
}
